import { inject, observer } from "mobx-react";
import classNames from "classnames";
import { CatalogKindTypes, placeholderImage } from "@ihr-radioedit/inferno-core";
import { ResponsiveImage, ResponsiveImageProps } from "../../components/ResponsiveImage.component";
import styles from "./PodcastEpisode.module.scss";
import { Store } from "../../../../client/stores";
import { PlayIcon } from "../../components/icons/PlayIcon.component";
import { Fragment, useMemo } from "react";
import { StopIcon } from "../../components/icons/StopIcon.component";
import { Spinner } from "../../ui";
import DateDisplay from "../../components/DateDisplay.component";
import { DATETIME_DISPLAY_FORMATS } from "../../lib/time";

const PodcastEpisodeListenNow = ({
    episodeMatch,
    inProgress,
    playback,
    text,
}: {
    inProgress: boolean;
    episodeMatch: boolean;
    playback: boolean;
    text: string;
}) => {
    const ListenState = (Comp: () => JSX.Element) => () =>
        (
            <Fragment>
                <Comp />
                <figcaption className={styles.listenNowText}> {text} </figcaption>
            </Fragment>
        );

    if (!episodeMatch) {
        return ListenState(PlayIcon)();
    }

    if (inProgress) {
        return <Spinner visible={true} />;
    }

    return playback ? ListenState(StopIcon)() : ListenState(PlayIcon)();
};

export interface PodcastEpisodeProps {
    related_id?: number;
    id: number;
    slug: string;
    store: Store;
    url: string;
    imageUrl: string;
    title: string;
    description: string;
    responsiveImg?: ResponsiveImageProps | null;
    startDate?: number;
}

export const PodcastEpisode = inject("store")(
    observer((props: PodcastEpisodeProps) => {
        const image = props.responsiveImg ? (
            <ResponsiveImage {...props.responsiveImg} />
        ) : (
            <img src={placeholderImage} data-src={props.imageUrl} alt={props.title} className="lazyload" />
        );

        const { playback, inProgress, overrideId } = props.store.player;

        const episodeMatch = useMemo(
            () => overrideId === props.related_id || overrideId === props.slug,
            [overrideId, props.related_id, props.slug],
        );

        const listenNowStyle = classNames(styles.listenNow, styles.iconRight);

        return (
            <div className={`${styles.episode} square-thumb elevation-01`}>
                <figure className={styles.thumbnail}>{image}</figure>
                <span className={styles.descriptionWrapper}>
                    <h3 className={styles.title}>{props.title}</h3>
                    {props.description && (
                        <div key={props.related_id} dangerouslySetInnerHTML={{ __html: props.description }} />
                    )}
                    {props.startDate && (
                        <DateDisplay
                            datetime={props.startDate}
                            dateFormat={DATETIME_DISPLAY_FORMATS.FULL_MONTH_SHORT_DAY_FULL_YEAR}
                        />
                    )}
                    <figure
                        className={listenNowStyle}
                        onClick={() =>
                            props.store.player.togglePlayback(props.store, {
                                slug: props.slug,
                                sectionName: "podcast-episode",
                                override: {
                                    id: props.id,
                                    related_id: props.related_id,
                                    kind: CatalogKindTypes.podcast,
                                },
                            })
                        }
                    >
                        <PodcastEpisodeListenNow
                            episodeMatch={episodeMatch}
                            playback={playback}
                            inProgress={inProgress}
                            text="Listen Now"
                        />
                    </figure>
                </span>
            </div>
        );
    }),
);
